import { useMemo, useState } from 'react';

import type { IssueCard, Member, Priority, Sprint } from '@tasks/contracts';

import { Button } from '../../components/Button';
import { Select, type SelectOption } from '../../components/Select';
import { PRIORITY_ORDER, priorityLabels } from '../../i18n/enums';
import { PRIORITY_COLOR_VAR } from '../../lib/types';
import styles from './BulkActionsBar.module.css';

const PRIORITY_OPTIONS: readonly SelectOption<Priority>[] = PRIORITY_ORDER.map((value) => ({
  value,
  label: priorityLabels[value],
  tone: PRIORITY_COLOR_VAR[value],
}));

export type BulkActionsBarProps = {
  readonly selected: readonly IssueCard[];
  readonly members: readonly Member[];
  readonly sprints: readonly Sprint[];
  readonly onClear: () => void;
  readonly onAssign: (assigneeId: string | null) => void;
  readonly onPriority: (priority: Priority) => void;
  readonly onMoveToSprint: (sprintId: string) => void;
  readonly onDelete: () => void;
};

/** Панель массовых действий снизу (docs/06 §3.3, UC-ISS-08). */
export function BulkActionsBar({
  selected,
  members,
  sprints,
  onClear,
  onAssign,
  onPriority,
  onMoveToSprint,
  onDelete,
}: BulkActionsBarProps) {
  const [targetSprint, setTargetSprint] = useState<string | null>(null);

  const memberOptions = useMemo<readonly SelectOption<string>[]>(
    () => members.map((member) => ({ value: member.user.id, label: member.user.displayName })),
    [members],
  );
  const sprintOptions = useMemo<readonly SelectOption<string>[]>(
    () => sprints.filter((sprint) => sprint.state === 'future').map((sprint) => ({ value: sprint.id, label: sprint.name })),
    [sprints],
  );

  if (selected.length === 0) {
    return null;
  }

  return (
    <div className={styles['bar']} role="toolbar" aria-label="Массовые действия">
      <span className={styles['count']}>Выбрано: {selected.length}</span>

      <div className={styles['group']}>
        <Select
          options={memberOptions}
          value={null}
          clearable
          placeholder="Исполнитель"
          onClear={() => onAssign(null)}
          onChange={onAssign}
        />
        <Select options={PRIORITY_OPTIONS} value={null} placeholder="Приоритет" onChange={onPriority} />
        {sprintOptions.length > 0 ? (
          <Select
            options={sprintOptions}
            value={targetSprint}
            placeholder="В спринт"
            onChange={(value) => {
              setTargetSprint(value);
              onMoveToSprint(value);
            }}
          />
        ) : null}
      </div>

      <span className={styles['spacer']} />

      <Button size="sm" variant="ghost" onClick={onClear}>
        Снять выделение
      </Button>
      <Button size="sm" variant="danger" onClick={onDelete}>
        Удалить
      </Button>
    </div>
  );
}
